import { useState, useEffect, useRef } from "react";
import { Box, useBreakpointValue } from "@chakra-ui/react";
import Chatbot from "./Chatbot";
import MiniSidebar from "./MiniSidebar";

type ParentProps = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onMobileClose?: () => void;
};

const SideNav: React.FC<ParentProps> = ({ isOpen, setIsOpen, onMobileClose }) => {
  const [activePanel, setActivePanel] = useState<"menu" | "chat" | null>(isOpen ? "menu" : null);
  const isMobile = useBreakpointValue({ base: true, lg: false });
  const prevIsOpenRef = useRef<boolean | undefined>(undefined);

  // Sync activePanel with isOpen prop changes
  useEffect(() => {
    const prevIsOpen = prevIsOpenRef.current;
    
    if (!isOpen) {
      setActivePanel(null);
    } else if (isOpen && (prevIsOpen === undefined || !prevIsOpen)) {
      setActivePanel("menu");
    }
    
    prevIsOpenRef.current = isOpen;
  }, [isOpen, isMobile]);
  
  // Handle panel toggle
  const handlePanelToggle = (panel: "menu" | "chat") => {
    if (isMobile) {
      setIsOpen(true);
      setActivePanel(activePanel === panel ? null : panel);
    } else {
      if (activePanel === panel) {
        setActivePanel(null);
        setIsOpen(false);
      } else {
        setActivePanel(panel);
        setIsOpen(true);
      }
    }
  };

  const handleChatbotClose = () => {
    setActivePanel(null);
    setIsOpen(false);
  };

  // Mobile View
  if (isMobile) {
    return (
      <>
        {/* Mini Sidebar - Always visible on mobile */}
        <MiniSidebar
          activePanel={activePanel}
          isOpen={isOpen}
          onMenuClick={() => handlePanelToggle("menu")}
          onChatClick={() => handlePanelToggle("chat")}
          zIndex={2}
        />

        {/* Chatbot Panel - Overlay on mobile */}
        {activePanel === "chat" && isOpen && (
          <>
            <Box
              position="fixed"
              top={0}
              left="80px"
              bottom={0}
              backgroundColor="black"
              color="white"
              width="350px"
              zIndex={1000}
              display="flex"
              flexDirection="column"
              borderRight="1px solid #333"
              boxShadow="2xl"
            >
              <Chatbot
              />
            </Box>
            {/* Overlay backdrop */}
            <Box
              position="fixed"
              top={0}
              left="430px"
              right={0}
              bottom={0}
              bg="blackAlpha.600"
              zIndex={999}
              onClick={() => {
                setIsOpen(false);
                if (onMobileClose) onMobileClose();
              }}
            />
          </>
        )}
      </>
    );
  }

  // Desktop View
  return (
    <>
      {/* Mini Sidebar - Always visible */}
      <MiniSidebar
        activePanel={activePanel}
        isOpen={isOpen}
        onMenuClick={() => handlePanelToggle("menu")}
        onChatClick={() => handlePanelToggle("chat")}
      />

      {/* Chatbot Panel */}
      {activePanel === "chat" && isOpen && (
        <Box
          backgroundColor="black"
          color="white"
          minH="100vh"
          width="350px"
          borderRight="1px solid #333"
          transition="all 0.3s ease"
          display="flex"
          flexDirection="column"
          position="relative"
          flexShrink={0}
        >
          <Chatbot  />
        </Box>
      )}
    </>
  );
};

export default SideNav;
